import { CompanyNameId } from "./Company";
import { ContactInformation } from "./ContactInformation";
import { translationKey } from "./i18n";

interface SearchResultCompany {
	ytunnus: string;
	yrityksennimi: string;
	nimentyyppi: string;
	yrityksenStatus: "L" | "S" | "J" | "A" | "K" | null;
}

export enum PreconditionFailureReason {
	EXISTING_CUSTOMER = "EXISTING_CUSTOMER",
	ACTIVITY_INTERRUPTED = "ACTIVITY_INTERRUPTED",
	BANKRUPT = "BANKRUPT",
	LIQUIDATION = "LIQUIDATION",
	COMPANY_FORM = "COMPANY_FORM",
	COMPANY_INDUSTRY = "COMPANY_INDUSTRY",
	TURNOVER_TOO_HIGH = "TURNOVER_TOO_HIGH",
	REGISTRATION_INCOMPLETE = "REGISTRATION_INCOMPLETE",
	CREDIT_RISK = "CREDIT_RISK",
	NO_SIGNATURE_RIGHT = "NO_SIGNATURE_RIGHT"
}

export interface CompanyCheckResult {
	validCompany: boolean;
	failureReason?: PreconditionFailureReason;
}

export interface BasketFormState {
	companyDisplayName: string;
	businessId: string;
	companyName: string;
	streetAddress: string;
	postalCode: string;
	city: string;
	firstName: string;
	lastName: string;
	billingStreetAddress: string;
	billingPostalCode: string;
	billingCity: string;
	onlineBillingAddress: string;
	termsUrl: string;
	preconditionFailureReason?: PreconditionFailureReason;
	authenticationResult?: AuthenticationResult
}

export enum BillingLanguage {
	FI = "FI",
	SV = "SV",
	EN = "EN"
}

export enum BasketType {
	Micro = "MICRO",
	Small = "SMALL",
	Compact = "COMPACT"
}

export enum BasketBillingType {
	Online = "ONLINE",
	Email = "EMAIL",
	Paper = "PAPER"
}

export enum AuthenticationResult {
	SUCCESS = "SUCCESS",
	CANCELED = "CANCELED",
	ERROR = "ERROR"
}

export interface BasketForm {
	companyDisplayName: string;
	businessId: string;
	companyName: string;
	streetAddress: string;
	postalCode: string;
	city: string;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	billingStreetAddress: string;
	billingPostalCode: string;
	billingCity: string;
	billingLanguage: BillingLanguage;
	basketType: BasketType;
	userAccountForSubscriber: boolean;
	billingType: BasketBillingType;
	onlineBillingAddress?: string;
	billingEmail?: string;

	user1FirstName: string;
	user1LastName: string;
	user1Email: string;

	user2FirstName: string;
	user2LastName: string;
	user2Email: string;

	user3FirstName: string;
	user3LastName: string;
	user3Email: string;

	user4FirstName: string;
	user4LastName: string;
	user4Email: string;

	termsUrl: string;
}

export interface BasketOrderForm {
	businessId: string;
	companyName: string;
	streetAddress: string;
	postalCode: string;
	city: string;
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
	billingStreetAddress: string;
	billingPostalCode: string;
	billingCity: string;
	billingLanguage: BillingLanguage;
	basketType: BasketType;
	userAccountForSubscriber: boolean;
	billingType: BasketBillingType;
	onlineBillingAddress?: string;
	billingEmail?: string;

	user1FirstName: string;
	user1LastName: string;
	user1Email: string;

	user2FirstName: string;
	user2LastName: string;
	user2Email: string;

	user3FirstName: string;
	user3LastName: string;
	user3Email: string;

	user4FirstName: string;
	user4LastName: string;
	user4Email: string;
}

interface PlaceOrderResultSuccess {
	success: true;
}

interface PlaceOrderResultFailure {
	success: false;
	errorMessage: translationKey;
}

export type PlaceOrderResult = PlaceOrderResultSuccess | PlaceOrderResultFailure;

export class BasketService {
	public async searchCompanies(searchText: string, abortController?: AbortController): Promise<SearchResultCompany[]> {
		const response = await fetch(`/autom_asiakas/BasketsHaku?hakuehto=${searchText}`, {
			method: "GET",
			headers: {
				"Content-type": "application/x-www-form-urlencoded; charset=utf-8"
			},
			...(abortController && {
				signal: abortController.signal
			})
		});

		if (!response.ok) {
			return [];
		}

		const responseText = await response.text();
		if (!responseText || responseText.length === 0) {
			return [];
		}

		const companies: SearchResultCompany[] | null = JSON.parse(responseText) as SearchResultCompany[] | null;
		return companies ?? [];
	}

	public async checkCompany(company: CompanyNameId): Promise<CompanyCheckResult | null> {
		const response = await fetch("/autom_asiakas/BasketsCheckCompany", {
			method: "POST",
			body: JSON.stringify({
				displayName: company.displayName,
				businessId: company.id,
				name: company.name
			}),
			headers: {
				"Content-type": "application/json"
			}
		});

		if (!response.ok) {
			return null;
		}

		return (await response.json()) as CompanyCheckResult;
	}

	public async sendContactInformation(contactInformation: ContactInformation): Promise<boolean> {
		const response = await fetch("/autom_asiakas/BasketsContact", {
			method: "POST",
			body: JSON.stringify({
				businessId: contactInformation.companyId,
				companyName: contactInformation.companyName,
				firstName: contactInformation.firstName,
				lastName: contactInformation.lastName,
				phone: contactInformation.phone,
				email: contactInformation.email
			}),
			headers: {
				"Content-type": "application/json"
			}
		});

		if (!response.ok) {
			return false;
		}

		return (await response.json()) as boolean;
	}

	public async getFormState(): Promise<BasketFormState | null> {
		const response = await fetch("/autom_asiakas/BasketsFormState", {
			method: "GET"
		});

		if (!response.ok) {
			return null;
		}

		const responseText = await response.text();
		if (!responseText || responseText.length === 0) {
			return null;
		}

		return JSON.parse(responseText) as BasketFormState;
	}

	public async placeOrder(order: BasketOrderForm): Promise<PlaceOrderResult> {
		const response = await fetch("/autom_asiakas/BasketsOrder", {
			method: "POST",
			body: JSON.stringify({
				businessId: order.businessId,
				companyName: order.companyName,
				streetAddress: order.streetAddress,
				postalCode: order.postalCode,
				city: order.city,
				firstName: order.firstName,
				lastName: order.lastName,
				phone: order.phone,
				email: order.email,
				billingStreetAddress: order.billingStreetAddress,
				billingPostalCode: order.billingPostalCode,
				billingCity: order.billingCity,
				billingLanguage: order.billingLanguage,
				basketType: order.basketType,
				userAccountForSubscriber: order.userAccountForSubscriber,
				billingType: order.billingType,
				onlineBillingAddress: order.onlineBillingAddress,
				billingEmail: order.billingEmail,

				user1FirstName: order.user1FirstName,
				user1LastName: order.user1LastName,
				user1Email: order.user1Email,

				user2FirstName: order.user2FirstName,
				user2LastName: order.user2LastName,
				user2Email: order.user2Email,

				user3FirstName: order.user3FirstName,
				user3LastName: order.user3LastName,
				user3Email: order.user3Email,

				user4FirstName: order.user4FirstName,
				user4LastName: order.user4LastName,
				user4Email: order.user4Email
			}),
			headers: {
				"Content-type": "application/json"
			}
		});

		if (!response.ok) {
			return {
				success: false,
				errorMessage: "orderFailed"
			};
		}

		return (await response.json()) as PlaceOrderResult;
	}
}

export const basketService = new BasketService();
